import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styled from "styled-components";

const Container = styled.div`
    position: relative;
    width: ${(props) => props.width || "fit-content"};
    display: inline-flex;
`;

const DropdownBox = styled.div`
    position: fixed;
    top: ${(props) => props.y}px;
    left: ${(props) => props.x}px;
    width: ${(props) => props.contentWidth};
    min-width: ${(props) => props.contentMinWidth};
    visibility: ${(props) => (props.open ? "visible" : "hidden")};
    opacity: ${(props) => (props.open ? "1" : "0")};
    z-index: ${(props) => props.zIndex || 100};
`;

function Dropdown(props) {
    const {
        // 드롭다운을 여는 기준이 되는 앵커 엘리먼트
        anchor,
        // 드롭다운 패널 내부에 렌더링할 내용
        children,
        // 외부에서 제어하는 열림 상태
        open,
        onOpenChange,
        // 지원값: bottom-start, bottom-center, bottom-end, top-start, top-center, top-end
        placement = "bottom-end",
        // 앵커와 패널 사이 간격(px)
        offset = 4,
        // 앵커 래퍼의 너비
        width,
        zIndex,
        // 기본값은 portal 사용. 부모 overflow에 잘리지 않도록 body에 렌더링
        usePortal = true,
        // 패널 width/min-width. "anchor"를 주면 앵커 너비와 동일하게 맞춤
        contentWidth,
        contentMinWidth,
    } = props;

    const containerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const resolveSize = (value, fallback) => {
        if (value === "anchor") {
            return fallback;
        }

        return value;
    };

    const calculatePosition = () => {
        if (!containerRef.current || !dropdownRef.current) return;

        const anchorRect = containerRef.current.getBoundingClientRect();
        const dropdownWidth = dropdownRef.current.offsetWidth || 0;
        const dropdownHeight = dropdownRef.current.offsetHeight || 0;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let x = anchorRect.right - dropdownWidth;
        let y = anchorRect.bottom + offset;

        // 우선 placement 기준으로 배치한 뒤,
        // 최종 위치는 viewport 안으로 보정한다.
        if (placement === "bottom-start") {
            x = anchorRect.left;
            y = anchorRect.bottom + offset;
        } else if (placement === "bottom-center") {
            x = anchorRect.left + anchorRect.width / 2 - dropdownWidth / 2;
            y = anchorRect.bottom + offset;
        } else if (placement === "top-start") {
            x = anchorRect.left;
            y = anchorRect.top - dropdownHeight - offset;
        } else if (placement === "top-center") {
            x = anchorRect.left + anchorRect.width / 2 - dropdownWidth / 2;
            y = anchorRect.top - dropdownHeight - offset;
        } else if (placement === "top-end") {
            x = anchorRect.right - dropdownWidth;
            y = anchorRect.top - dropdownHeight - offset;
        } else {
            x = anchorRect.right - dropdownWidth;
            y = anchorRect.bottom + offset;
        }

        if (x + dropdownWidth > viewportWidth - offset) {
            x = viewportWidth - dropdownWidth - offset;
        }
        if (x < offset) {
            x = offset;
        }

        if (y + dropdownHeight > viewportHeight - offset) {
            y = anchorRect.top - dropdownHeight - offset;
        }
        if (y < offset) {
            y = offset;
        }

        setPosition({ x, y });
    };

    useLayoutEffect(() => {
        if (!open) return;
        calculatePosition();
    }, [open, placement, children]);

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event) => {
            const isAnchorClick = containerRef.current?.contains(event.target);
            const isDropdownClick = dropdownRef.current?.contains(event.target);

            if (!isAnchorClick && !isDropdownClick) {
                onOpenChange(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onOpenChange(false);
            }
        };

        const handleReposition = () => {
            calculatePosition();
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("resize", handleReposition);
        document.addEventListener("scroll", handleReposition, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("resize", handleReposition);
            document.removeEventListener("scroll", handleReposition, true);
        };
    }, [open, onOpenChange]);

    const anchorWidth = `${containerRef.current?.getBoundingClientRect().width || 0}px`;
    const content = (
        <DropdownBox
            open={open}
            x={position.x}
            y={position.y}
            ref={dropdownRef}
            zIndex={zIndex}
            contentWidth={resolveSize(contentWidth, anchorWidth)}
            contentMinWidth={resolveSize(contentMinWidth, anchorWidth)}
        >
            {children}
        </DropdownBox>
    );

    return (
        <Container width={width} ref={containerRef}>
            {anchor}
            {usePortal ? createPortal(content, document.body) : content}
        </Container>
    );
}

export default Dropdown;
